import React from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { InfiniteApplication, InfiniteApplicationApplicant } from '~/services/LuxLoyaltyInfiniteApplicationService';

import { getAddress, getDisplayName, getPhone } from './infiniteApplicationReview.utils';

export default function FinalizeApplicationDialog({
  application,
  primaryApplicant,
  existingProfilesForApplicants,
  existingBookingAuthoritiesForApplicants,
  notes,
  open,
  canFinalize,
  isMutating,
  onClose,
  onNotesChange,
  onConfirm,
}: {
  application: InfiniteApplication | undefined;
  primaryApplicant: InfiniteApplicationApplicant | undefined;
  existingProfilesForApplicants: Array<{
    applicant: InfiniteApplicationApplicant;
    profile: App.LuxLoyaltyInfiniteProfile;
  }>;
  existingBookingAuthoritiesForApplicants: Array<{
    applicant: InfiniteApplicationApplicant;
    bookingAuthorities: Array<App.LuxLoyaltyInfiniteBookingAuthority>;
  }>;
  notes: string;
  open: boolean;
  canFinalize: boolean;
  isMutating: boolean;
  onClose: () => void;
  onNotesChange: (notes: string) => void;
  onConfirm: () => void;
}) {
  const applicationBookingAuthorities = application?.bookingAuthorities ?? [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Finalize application</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Chip
            color="success"
            icon={<CheckCircleIcon />}
            label="Ready to finalize"
            sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
          />
          <Typography variant="body1">You are about to finalize the application from:</Typography>
          {primaryApplicant && (
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {getDisplayName(primaryApplicant)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {primaryApplicant.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getPhone(primaryApplicant)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getAddress(primaryApplicant) || '-'}
              </Typography>
            </Box>
          )}
          <Typography variant="body2">
            This will link all applicants to their Infinite accounts and mark the application as finalised.
          </Typography>
          {existingProfilesForApplicants.length > 0 && (
            <Alert severity="warning">
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Existing Infinite profiles are already associated with these accounts:
              </Typography>
              <Typography variant="body2" color="error.main" sx={{ fontWeight: 700, mb: 0.5 }}>
                Warning: confirming will overwrite the existing profile.
              </Typography>
              <Box component="ul" sx={{ pl: 2, my: 0 }}>
                {existingProfilesForApplicants.map(({ applicant, profile }) => (
                  <Box component="li" key={profile.id}>
                    {getDisplayName(applicant)} ({applicant.role}) - customer {profile.customerId}
                  </Box>
                ))}
              </Box>
            </Alert>
          )}
          {existingBookingAuthoritiesForApplicants.length > 0 && applicationBookingAuthorities.length > 0 && (
            <Alert severity="warning">
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Existing booking authorities are already associated with these profiles:
              </Typography>
              <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                {existingBookingAuthoritiesForApplicants.map(({ applicant, bookingAuthorities }) => (
                  <Box component="li" key={applicant.id}>
                    {getDisplayName(applicant)}
                    <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                      {bookingAuthorities.map((authority) => (
                        <Box component="li" key={authority.id}>
                          {getDisplayName(authority)} - {authority.email} - {getPhone(authority)}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Finalizing will add these new booking authorities from the current application:
              </Typography>
              <Box component="ul" sx={{ pl: 2, my: 0 }}>
                {applicationBookingAuthorities.map((authority) => (
                  <Box component="li" key={authority.id}>
                    {getDisplayName(authority)} - {authority.email} - {getPhone(authority)}
                  </Box>
                ))}
              </Box>
            </Alert>
          )}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
              Current note
            </Typography>
            <Typography
              variant="body2"
              color={application?.notes ? 'text.primary' : 'text.secondary'}
              sx={{ whiteSpace: 'pre-wrap' }}
            >
              {application?.notes || 'No notes recorded.'}
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={3}
            margin="dense"
            label="Finalize note"
            value={notes}
            onChange={(event) => onNotesChange(event.target.value)}
            helperText="Optional"
          />
          <Typography variant="body2">Are you sure?</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isMutating}>
          Cancel
        </Button>
        <Button variant="contained" color="success" onClick={onConfirm} disabled={!canFinalize || isMutating}>
          Confirm finalize
        </Button>
      </DialogActions>
    </Dialog>
  );
}
